import { ChevronDown } from "lucide-react";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useConfigStore } from "../../store/configStore";
import { useEditorStore } from "../../store/editorStore";
import useSWR from "swr";
import { SpineTemplate } from "../../types/spine-template";
import { ExportPreviewDialog } from "./ExportPreviewDialog";
import { DownloadSpineZipMenuItem } from "./components/DownloadSpineZipMenuItem";
import { ExportConfigMenuItems } from "./components/ExportConfigMenuItems";
import { ExportOutfitMenuItem } from "./components/ExportOutfitMenuItem";
import { ExportAnimationMenuSub } from "./components/export-animation";
import { ExportAnimationProgressDialog } from "./components/export-animation/ExportAnimationProgressDialog";
import type { AnimationExportFormat } from "./components/export-animation";

export function ExportDropdown() {
  const { tid } = useParams<{ tid: string }>();
  const { templateConfig, parsedSkins, currentTid } = useConfigStore();
  const {
    existingZipUrl,
    selectedSkins,
    selectedToningColors,
    currentAnimation,
    spineApp,
    spineCanvas,
  } = useEditorStore();
  const [exportPreviewOpen, setExportPreviewOpen] = useState(false);
  const [exportPreviewJson, setExportPreviewJson] = useState("");
  const [animationExportOpen, setAnimationExportOpen] = useState(false);
  const [animationExportProgress, setAnimationExportProgress] = useState(0);
  const [animationExportFormat, setAnimationExportFormat] =
    useState<AnimationExportFormat>("gif");

  const animationDialog = useMemo(
    () => ({
      open: animationExportOpen,
      setOpen: setAnimationExportOpen,
      progress: animationExportProgress,
      setProgress: setAnimationExportProgress,
      format: animationExportFormat,
      setFormat: setAnimationExportFormat,
    }),
    [animationExportOpen, animationExportProgress, animationExportFormat],
  );

  const { data: template } = useSWR<SpineTemplate>(
    tid ? [`/spine-templates/${tid}`] : null,
  );

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="default" className="gap-1">
            导出
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {/* 下载 Spine ZIP */}
          <DownloadSpineZipMenuItem
            zipUrl={existingZipUrl}
            templateId={tid}
          />
          {/* 导出预览 导出配置 */}
          <ExportConfigMenuItems
            templateConfig={templateConfig}
            template={template}
            parsedSkins={parsedSkins}
            currentTid={currentTid}
            onExportPreviewJson={(json) => {
              setExportPreviewJson(json);
              setExportPreviewOpen(true);
            }}
          />
          {/* 导出搭配 */}
          <ExportOutfitMenuItem
            templateConfig={templateConfig}
            selectedSkins={selectedSkins}
            selectedToningColors={selectedToningColors}
            currentAnimation={currentAnimation}
            templateId={tid}
          />
          {/* 导出动图 */}
          <ExportAnimationMenuSub
            spineApp={spineApp}
            spineCanvas={spineCanvas}
            templateId={tid}
            dialog={animationDialog}
          />
        </DropdownMenuContent>
      </DropdownMenu>

      <ExportPreviewDialog
        open={exportPreviewOpen}
        onOpenChange={setExportPreviewOpen}
        titleSuffix={`neku-${currentTid || "config"}.json`}
        json={exportPreviewJson}
      />

      <ExportAnimationProgressDialog
        open={animationExportOpen}
        format={animationExportFormat}
        progress={animationExportProgress}
      />
    </>
  );
}
